/**
 * usePostStreamingState.ts - Post Streaming State Detection Hook
 *
 * Determines streaming state for a specific post using the streamingComplete
 * flag from ChatContext, which is set directly by SSE events.
 */

import { useStreamingState } from '@/contexts/ChatContext';

interface PostStreamingStateResult {
    /** True only while actively streaming content for this post */
    isStreaming: boolean;
    /** True if this post is the current streaming target */
    isStreamingPost: boolean;
    /** True when streaming completed for this post (SSE onComplete fired) */
    hasStreamingEnded: boolean;
    /** The current streaming text */
    streamingText: string;
}

interface UsePostStreamingStateOptions {
    postId: string;
}

/**
 * Hook to determine the streaming state for a specific post.
 * 
 * Uses the streamingComplete flag from ChatContext which is set
 * directly by the SSE onComplete event for reliable detection.
 */
export function usePostStreamingState({
    postId,
}: UsePostStreamingStateOptions): PostStreamingStateResult {
    const { streamingAssistantPostId, streamingText, streamingComplete } = useStreamingState();
    
    const isStreamingPost = streamingAssistantPostId === postId;
    const isStreaming = isStreamingPost && streamingText.length > 0 && !streamingComplete;
    const hasStreamingEnded = isStreamingPost && streamingComplete;

    return {
        isStreaming,
        isStreamingPost,
        hasStreamingEnded,
        streamingText,
    };
}
